import React, { useCallback, useContext, useEffect, useState, FC, createContext } from 'react';
import { useHistory } from 'react-router';
import Modal from '../components/Modal/NewModal';

interface PendingChangesPromptProps {
  onDiscard: () => void;
  onCancel: () => void;
}

export const PendingChangesContext = createContext({
  isChanged: false,
  showPrompt: false,
  setIsChanged: (state: boolean) => {},
  setShowPrompt: (state: boolean) => {},
  checkAndPrompt: (callback?: () => any) => {}
});

export const PendingChangesPrompt: FC<PendingChangesPromptProps> = ({ onDiscard, onCancel }) => {
  const { showPrompt, setShowPrompt } = useContext(PendingChangesContext);

  return (
    <Modal
      isOpen={showPrompt}
      onClose={() => setShowPrompt(false)}
      header="You have pending changes"
      actions={[
        { title: 'Cancel', kind: 'secondary', onClick: onCancel },
        { title: 'Discard changes and continue', kind: 'primary', onClick: onDiscard }
      ]}
      size="default"
    >
      <div data-testid="pending-modal" className="px-8 pt-6 h-48 text-xl">
        Do you want to save your changes? Your changes will be lost if you continue without saving.
      </div>
    </Modal>
  );
};

export const PendingChangesContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [navigateCallback, setNavigateCallback] = useState<any>(() => {});

  const history = useHistory();

  const checkAndPrompt = useCallback(
    (callback) => {
      if (isChanged) {
        setShowPrompt(true);
        if (callback) {
          setNavigateCallback(() => callback);
        }
      } else callback();
    },
    [isChanged]
  );

  useEffect(() => {
    const unblock = history.block((location) => {
      if (isChanged) {
        checkAndPrompt(() => {
          unblock();
          history.push(location.pathname);
        });
        return false;
      }
      return unblock();
    });
    return () => unblock();
  }, [checkAndPrompt, history, isChanged]);

  const onDiscard = () => {
    navigateCallback();
    setIsChanged(false);
    setNavigateCallback(false);
  };

  const onCancel = () => {
    setNavigateCallback(false);
  };

  return (
    <PendingChangesContext.Provider value={{ isChanged, setIsChanged, showPrompt, setShowPrompt, checkAndPrompt }}>
      {children}
      <PendingChangesPrompt onDiscard={onDiscard} onCancel={onCancel} />
    </PendingChangesContext.Provider>
  );
};
